/**
 * GlobalLinkPreviewPanel Component
 *
 * Renders a restored URL preview panel as a global overlay.
 * Can be opened from any page when a minimized panel is clicked.
 *
 * Features:
 * - Resizable panel width (drag left edge)
 * - Fullscreen mode
 * - Re-minimize back to dock
 * - Open in new tab
 * - Blur background with backdrop click to close
 * - Keyboard support (Escape cycles through states)
 * - Internal URLs (cenari.net, *.eroland.me) use iframe
 * - External URLs show fallback UI with "Open in New Tab" option
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useMinimizedUrlPanelsStore } from '@shared/store/minimizedUrlPanelsStore';
import { useUIStore } from '@shared/store/uiStore';
import { RemoteBrowserPanel } from '@shared/components/RemoteBrowserPanel';

/** Minimum panel width percentage */
const MIN_PANEL_WIDTH = 20;
/** Maximum panel width percentage (when not expanded) */
const MAX_PANEL_WIDTH = 80;

/**
 * Determines if a URL is internal (can be rendered in iframe)
 * or external (requires remote browser rendering via server-side Chromium)
 */
function isInternalUrl(url: string): boolean {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    return (
      hostname === 'cenari.net' ||
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.endsWith('.eroland.me') ||
      hostname === 'eroland.me'
    );
  } catch {
    // If URL parsing fails, treat as internal (safer fallback)
    return true;
  }
}

/**
 * GlobalLinkPreviewPanel component
 */
export const GlobalLinkPreviewPanel: React.FC = () => {
  const {
    restoredPanel,
    closeRestoredPanel,
    reMinimizeRestoredPanel,
    setRestoredPanelFullscreen,
    setRestoredPanelWidth,
    renameRestoredPanelTitle,
  } = useMinimizedUrlPanelsStore();

  const themeClass = useUIStore((state) => state.themeClass);
  const isDynamicsTheme = themeClass === 'theme-dynamics';

  const [iframeError, setIframeError] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  // Title editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Determine if we should use remote browser
  // Use remote browser when explicitly set via isRemote flag, OR for external URLs
  const useRemoteBrowserPanel = useMemo(() => {
    if (!restoredPanel) return false;
    // If explicitly marked as remote, use remote browser
    if (restoredPanel.isRemote) return true;
    // For external URLs without isRemote flag, show fallback UI
    return false;
  }, [restoredPanel]);

  // Show fallback for external URLs that don't have isRemote flag
  const showExternalFallback = useMemo(() => {
    if (!restoredPanel?.url) return false;
    if (restoredPanel.isRemote) return false; // Remote browser handles external
    return !isInternalUrl(restoredPanel.url);
  }, [restoredPanel?.url, restoredPanel?.isRemote]);

  // Reset iframe error when panel changes
  useEffect(() => {
    if (restoredPanel) {
      setIframeError(false);
    }
  }, [restoredPanel?.url]);

  // Handle resize drag
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing || !restoredPanel) return;

    const handleMouseMove = (e: MouseEvent) => {
      const viewportWidth = window.innerWidth;
      const newWidth = ((viewportWidth - e.clientX) / viewportWidth) * 100;
      // Clamp between MIN and MAX
      setRestoredPanelWidth(Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, restoredPanel, setRestoredPanelWidth]);

  // Handle open in new tab
  const handleOpenInNewTab = useCallback(() => {
    if (restoredPanel) {
      window.open(restoredPanel.url, '_blank', 'noopener,noreferrer');
    }
  }, [restoredPanel]);

  // Handle keyboard - Escape cycles through states
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!restoredPanel) return;

      if (e.key === 'Escape') {
        if (restoredPanel.isFullscreen) {
          // Exit fullscreen first
          setRestoredPanelFullscreen(false);
        } else {
          // Close from normal state
          closeRestoredPanel();
        }
      }
    },
    [restoredPanel, setRestoredPanelFullscreen, closeRestoredPanel]
  );

  // Handle iframe load error
  const handleIframeError = useCallback(() => {
    setIframeError(true);
  }, []);

  // Handle title double-click to edit
  const handleTitleDoubleClick = useCallback(() => {
    if (!restoredPanel) return;
    const effectiveTitle = restoredPanel.customTitle || restoredPanel.title;
    setEditTitleValue(effectiveTitle.replace(/<[^>]*>/g, '')); // Strip HTML
    setIsEditingTitle(true);
  }, [restoredPanel]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // Handle title edit submission
  const handleTitleSubmit = useCallback(() => {
    const trimmed = editTitleValue.trim();
    if (trimmed && restoredPanel) {
      const effectiveTitle = (restoredPanel.customTitle || restoredPanel.title).replace(/<[^>]*>/g, '');
      if (trimmed !== effectiveTitle) {
        renameRestoredPanelTitle(trimmed);
      }
    }
    setIsEditingTitle(false);
  }, [editTitleValue, restoredPanel, renameRestoredPanelTitle]);

  // Handle title edit cancel
  const handleTitleCancel = useCallback(() => {
    setIsEditingTitle(false);
    setEditTitleValue('');
  }, []);

  // Handle title input key events
  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleTitleCancel();
    }
  }, [handleTitleSubmit, handleTitleCancel]);

  // Toggle fullscreen
  const handleToggleFullscreen = useCallback(() => {
    if (restoredPanel) {
      setRestoredPanelFullscreen(!restoredPanel.isFullscreen);
    }
  }, [restoredPanel, setRestoredPanelFullscreen]);

  // For remote browser panels: keep mounted but hidden when visually minimized
  // This preserves the browser session (no disconnect on minimize)
  const isHidden = restoredPanel?.isVisuallyMinimized === true;

  if (!restoredPanel) return null;

  // Use customTitle if set, otherwise fall back to default title
  // Strip HTML from title for display
  const displayTitle = (restoredPanel.customTitle || restoredPanel.title).replace(/<[^>]*>/g, '');

  // Calculate panel width style
  const getPanelWidthStyle = (): React.CSSProperties => {
    if (restoredPanel.isFullscreen) {
      return { width: '100%' };
    }
    return { width: `${restoredPanel.panelWidth}%` };
  };

  const panel = (
    <div
      className="fixed inset-0 z-[200] flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby="global-link-preview-title"
      onKeyDown={handleKeyDown}
      style={{
        // When visually minimized, hide via CSS but keep mounted (preserves remote browser session)
        visibility: isHidden ? 'hidden' : 'visible',
        pointerEvents: isHidden ? 'none' : 'auto',
      }}
    >
      {/* Backdrop with blur */}
      <div
        className={`
          absolute inset-0 transition-opacity duration-200
          ${restoredPanel.isFullscreen ? 'bg-black/60' : 'bg-black/40'}
          backdrop-blur-sm
        `}
        onClick={closeRestoredPanel}
        aria-hidden="true"
      />

      {/* Slide-out panel */}
      <div
        className={`
          relative h-full
          transform transition-all duration-200 ease-out
          ${isDynamicsTheme
            ? 'bg-dark-surface border-l border-dark-border/30'
            : 'bg-dark-bg border-l border-dark-border/30'
          }
          shadow-2xl
          ${isResizing ? 'select-none' : ''}
        `}
        style={getPanelWidthStyle()}
      >
        {/* Resize handle (left edge) - only show when not expanded */}
        {!restoredPanel.isFullscreen && (
          <div
            className={`
              absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize
              hover:bg-brand-700/50 active:bg-blue-500
              transition-all duration-150 z-10
              ${isResizing ? 'bg-blue-500' : ''}
            `}
            onMouseDown={handleResizeMouseDown}
            title="Drag to resize"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize panel"
          />
        )}

        <div className="flex flex-col h-full">
          {/* Header */}
          <div
            className={`
              flex items-center justify-between p-4 border-b
              ${isDynamicsTheme ? 'border-dark-border/30' : 'border-dark-border/30'}
            `}
          >
            <div className="flex-1 min-w-0">
              {isEditingTitle ? (
                <input
                  ref={titleInputRef}
                  type="text"
                  value={editTitleValue}
                  onChange={(e) => setEditTitleValue(e.target.value)}
                  onBlur={handleTitleSubmit}
                  onKeyDown={handleTitleKeyDown}
                  className={`
                    w-full text-lg font-semibold px-2 py-1 rounded
                    outline-none border-2
                    ${isDynamicsTheme
                      ? 'bg-dark-surface border-brand-500 text-white focus:ring-2 focus:ring-brand-400'
                      : 'bg-dark-surface border-brand-500 text-white focus:ring-2 focus:ring-brand-400'
                    }
                  `}
                  aria-label="Edit panel title"
                  data-testid="global-panel-title-input"
                />
              ) : (
                <h2
                  id="global-link-preview-title"
                  className={`text-lg font-semibold truncate cursor-pointer hover:opacity-80 ${isDynamicsTheme ? 'text-white' : 'text-white'}`}
                  onDoubleClick={handleTitleDoubleClick}
                  title="Double-click to rename"
                  data-testid="global-panel-title"
                >
                  {displayTitle}
                </h2>
              )}
              <p className={`text-xs truncate mt-0.5 ${isDynamicsTheme ? 'text-dark-muted' : 'text-dark-muted'}`}>
                {restoredPanel.url}
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-1 ml-4">
              {/* Minimize button */}
              <button
                type="button"
                onClick={reMinimizeRestoredPanel}
                className={`
                  p-2 rounded-lg transition-all duration-150 min-w-[44px] min-h-[44px] flex items-center justify-center
                  ${isDynamicsTheme
                    ? 'text-dark-muted hover:text-dark-text hover:bg-dark-input'
                    : 'text-dark-muted hover:text-white hover:bg-dark-surface'
                  }
                `}
                aria-label="Minimize to dock"
                title="Minimize to dock"
                data-testid="global-panel-minimize-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </button>

              {/* Fullscreen toggle button */}
              <button
                type="button"
                onClick={handleToggleFullscreen}
                className={`
                  p-2 rounded-lg transition-all duration-150 min-w-[44px] min-h-[44px] flex items-center justify-center
                  ${isDynamicsTheme
                    ? 'text-dark-muted hover:text-dark-text hover:bg-dark-input'
                    : 'text-dark-muted hover:text-white hover:bg-dark-surface'
                  }
                  ${restoredPanel.isFullscreen ? 'bg-blue-500/20' : ''}
                `}
                aria-label={restoredPanel.isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                title={restoredPanel.isFullscreen ? 'Exit fullscreen (Esc)' : 'Enter fullscreen'}
                data-testid="global-panel-fullscreen-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {restoredPanel.isFullscreen ? (
                    // Contract icon (exit fullscreen)
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                  ) : (
                    // Expand icon (enter fullscreen)
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                  )}
                </svg>
              </button>

              {/* Divider */}
              <div className={`w-px h-6 mx-1 ${isDynamicsTheme ? 'bg-dark-surface/50' : 'bg-dark-input'}`} />

              {/* Open in new tab */}
              <button
                type="button"
                onClick={handleOpenInNewTab}
                className={`
                  p-2 rounded-lg transition-all duration-150 min-w-[44px] min-h-[44px] flex items-center justify-center
                  ${isDynamicsTheme
                    ? 'text-dark-muted hover:text-dark-text hover:bg-dark-input'
                    : 'text-dark-muted hover:text-white hover:bg-dark-surface'
                  }
                `}
                aria-label="Open in new tab"
                title="Open in new tab"
                data-testid="global-panel-open-tab-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </button>

              {/* Close button */}
              <button
                type="button"
                onClick={closeRestoredPanel}
                className={`
                  p-2 rounded-lg transition-all duration-150 min-w-[44px] min-h-[44px] flex items-center justify-center
                  ${isDynamicsTheme
                    ? 'text-dark-muted hover:text-dark-text hover:bg-dark-input'
                    : 'text-dark-muted hover:text-white hover:bg-dark-surface'
                  }
                `}
                aria-label="Close panel"
                title="Close panel (Esc)"
                data-testid="global-panel-close-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content - Remote browser, external fallback, iframe, or error state */}
          <div className="flex-1 overflow-hidden">
            {useRemoteBrowserPanel ? (
              // Remote browser mode: Use RemoteBrowserPanel for server-side rendering
              // Pass isMinimized to throttle frames when panel is visually hidden
              <RemoteBrowserPanel
                url={restoredPanel.url}
                title={displayTitle}
                onNavigate={(newUrl) => {
                  // Update the panel URL when navigating in remote browser
                  // Note: This would require adding updateRestoredPanelUrl to the store
                }}
                onClose={closeRestoredPanel}
                isDynamicsTheme={isDynamicsTheme}
                isMinimized={isHidden}
              />
            ) : showExternalFallback ? (
              // External URL without isRemote: Show fallback UI
              <div className={`flex flex-col items-center justify-center h-full p-8 text-center ${isDynamicsTheme ? 'bg-dark-surface' : 'bg-dark-bg'}`}>
                {/* Globe icon for external link */}
                <svg
                  className={`w-20 h-20 mb-6 ${isDynamicsTheme ? 'text-blue-500' : 'text-blue-400'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                  />
                </svg>
                <h3 className={`text-xl font-semibold mb-3 ${isDynamicsTheme ? 'text-white' : 'text-white'}`}>
                  External Link
                </h3>
                <p className={`text-sm mb-2 max-w-md ${isDynamicsTheme ? 'text-dark-muted' : 'text-dark-muted'}`}>
                  This link goes to an external website that cannot be previewed in the app.
                </p>
                <p className={`text-xs mb-6 font-mono px-3 py-1.5 rounded ${isDynamicsTheme ? 'bg-dark-input text-dark-text' : 'bg-dark-surface text-dark-text'}`}>
                  {restoredPanel.url}
                </p>
                <button
                  type="button"
                  onClick={handleOpenInNewTab}
                  className="px-6 py-3 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-all duration-150 min-h-[44px] flex items-center gap-2 font-medium"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                  Open in New Tab
                </button>
              </div>
            ) : iframeError ? (
              <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <svg
                  className={`w-16 h-16 mb-4 ${isDynamicsTheme ? 'text-dark-text' : 'text-dark-muted'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <h3 className={`text-lg font-medium mb-2 ${isDynamicsTheme ? 'text-white' : 'text-white'}`}>
                  Unable to load preview
                </h3>
                <p className={`text-sm mb-4 ${isDynamicsTheme ? 'text-dark-muted' : 'text-dark-muted'}`}>
                  This website may not allow embedding. Try opening in a new tab instead.
                </p>
                <button
                  type="button"
                  onClick={handleOpenInNewTab}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-all duration-150 min-h-[44px]"
                >
                  Open in New Tab
                </button>
              </div>
            ) : (
              // Internal URLs: Use iframe
              <iframe
                src={restoredPanel.url}
                title={`Preview: ${displayTitle}`}
                className="w-full h-full border-0"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                onError={handleIframeError}
              />
            )}
          </div>

          {/* Width indicator (shown while resizing) */}
          {isResizing && (
            <div
              className={`
                absolute bottom-4 left-4 px-3 py-1.5 rounded-lg text-sm font-mono
                ${isDynamicsTheme ? 'bg-dark-input text-dark-text' : 'bg-dark-surface text-dark-text'}
              `}
            >
              {Math.round(restoredPanel.panelWidth)}%
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(panel, document.body);
};

export default GlobalLinkPreviewPanel;
